import React, { useEffect, useRef, useState } from "react";
import styles from "./Reveal.module.css";

/*
 * Scroll-triggered reveal — fades + slides content in as it enters the viewport,
 * the signature motion used across appinventiv.com. Uses IntersectionObserver,
 * reveals once, and is fully disabled under prefers-reduced-motion (handled in CSS).
 *
 * Props:
 *   as        — element/tag to render (default "div")
 *   direction — "up" | "left" | "right" | "scale" (default "up")
 *   delay     — stagger delay in ms
 */
function Reveal({
  children,
  as: Tag = "div",
  direction = "up",
  delay = 0,
  className = "",
  ...rest
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const classNames = [
    styles.reveal,
    styles[direction] || styles.up,
    visible ? styles.visible : "",
    className
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Tag
      ref={ref}
      className={classNames}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export default Reveal;
